import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Loader2, Play } from "lucide-react";

const BinocularViewer = lazy(
  () =>
    // @ts-expect-error JSX module without types
    import("@/components/BinocularViewer.jsx") as Promise<{
      default: React.ComponentType<{ modelSrc: string }>;
    }>,
);

type Props = {
  modelSrc: string;
  poster?: string;
};

/**
 * Renders a lightweight poster until either:
 *   (a) the user clicks "View in 3D", or
 *   (b) the section scrolls into the viewport (rootMargin 400px).
 * The heavy Three.js + drei bundle is only fetched on activation.
 */
export default function BinocularViewerLazy({ modelSrc, poster }: Props) {
  const [active, setActive] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (active) return;
    if (typeof IntersectionObserver === "undefined") return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          const idle =
            (window as unknown as { requestIdleCallback?: (cb: () => void) => number })
              .requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 1));
          idle(() => setActive(true));
          io.disconnect();
        }
      },
      { rootMargin: "400px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [active]);

  return (
    <div
      ref={ref}
      className="w-full bg-background border border-border"
      style={{ minHeight: 680 }}
    >
      {active ? (
        <Suspense
          fallback={
            <div className="w-full h-[680px] flex items-center justify-center bg-muted/40">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          }
        >
          <BinocularViewer modelSrc={modelSrc} />
        </Suspense>
      ) : (
        <div className="relative w-full h-[680px] bg-muted/40 flex items-center justify-center overflow-hidden">
          {poster ? (
            <img
              src={poster}
              alt="6×16 Binocular — click to inspect in 3D"
              width={1600}
              height={1067}
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover opacity-70"
            />
          ) : null}
          <button
            type="button"
            onClick={() => setActive(true)}
            className="relative z-10 inline-flex items-center gap-3 bg-foreground text-background font-mono text-[11px] uppercase tracking-[0.25em] px-6 py-3 hover:bg-foreground/90 transition-colors"
          >
            <Play className="w-4 h-4 fill-background" />
            View in 3D
          </button>
        </div>
      )}
    </div>
  );
}
